import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { DrawerType, ProjectType } from '@/stores/type/project.type'
import API from '@/api/apiUrls'
import { useApi } from '@/api/useApi'
import type { ProjectUserType } from '@/stores/type/projectUser.type'
import { useAuthorizationStore } from '@/stores/authorization'
import { useProjectUserStore } from '@/stores/projectUser'
import { useAuthenticationStore } from '@/stores/authentication'
import { useBaseStore } from '@haskoning/vue-basestore'

const api = useApi<ProjectType>()
const baseUrl = API.CORE.PROJECTS

const DEFAULT_DRAWER_WIDTH = 700
const DEFAULT_RESULT_DRAWER_WIDTH = 800

export const useProjectStore = defineStore('projects', () => {
	const baseStore = useBaseStore<ProjectType>({ service: baseUrl, api })

	const servicePermissionProjects = ref<any[]>([])
	const filterFields = ref(['name', 'location', 'client', 'description'])
	const drawer = ref({ shown: false, width: 250 })
	const calculationDrawer = ref({
		shown: false,
		width: DEFAULT_DRAWER_WIDTH,
		minWidth: 600,
		maxWidth: 900
	})
	const giDrawer = ref({
		shown: true,
		width: DEFAULT_DRAWER_WIDTH,
		minWidth: 600,
		maxWidth: 900
	})
	const resultDrawer = ref({
		shown: false,
		width: DEFAULT_RESULT_DRAWER_WIDTH,
		minWidth: 600,
		maxWidth: 900
	})
	const userManagementDrawer = ref({
		shown: false,
		width: DEFAULT_DRAWER_WIDTH,
		minWidth: 300,
		maxWidth: 900
	})
	const projectParameterDrawer = ref({
		shown: false,
		width: DEFAULT_DRAWER_WIDTH,
		minWidth: 300,
		maxWidth: 900
	})

	// Actions
	const resetAll = () => {
		baseStore.reset()
		servicePermissionProjects.value = []
		drawer.value.shown = false
		drawer.value.width = 250
		calculationDrawer.value.shown = false
		calculationDrawer.value.width = DEFAULT_DRAWER_WIDTH
		giDrawer.value.shown = true
		giDrawer.value.width = DEFAULT_DRAWER_WIDTH
		resultDrawer.value.shown = false
		resultDrawer.value.width = DEFAULT_RESULT_DRAWER_WIDTH
		userManagementDrawer.value.shown = false
		userManagementDrawer.value.width = DEFAULT_DRAWER_WIDTH
		projectParameterDrawer.value.shown = false
		projectParameterDrawer.value.width = DEFAULT_DRAWER_WIDTH
	}

	const resetDrawerWidth = (drawerObj: { width: number }) => {
		drawerObj.width = DEFAULT_DRAWER_WIDTH
	}

	const toggleDrawer = () => {
		drawer.value.shown = !drawer.value.shown
	}

	const toggleResultDrawer = (bool: boolean = true) => {
		resultDrawer.value.shown = !bool ? false : !resultDrawer.value.shown
	}

	const toggleDrawerType = (drawerType: DrawerType) => {
		const drawers = {
			calculationDrawer,
			giDrawer,
			resultDrawer,
			userManagementDrawer,
			projectParameterDrawer
		}
		Object.entries(drawers).forEach(([key, value]) => {
			value.value.shown = key === drawerType ? !value.value.shown : false
		})
	}

	const servicePermissionItems = async () => {
		const projectUserStore = useProjectUserStore()
		const authStore = useAuthenticationStore()
		const authorizationStore = useAuthorizationStore()

		const userId = authStore.user?.id
		await projectUserStore.fetchItems({ user: userId })

		const roleItems = projectUserStore.items.map((item: ProjectUserType) => ({
			role: item.role,
			id: item.project
		}))
		servicePermissionProjects.value = await authorizationStore.getServicePermissionByRole({
			roleItems,
			permission: ['change_project', 'delete_project']
		})
	}

	const getChangePermission = (id: number) => {
		if (!servicePermissionProjects.value.length) return true
		const permissions =
			servicePermissionProjects.value.find((item) => item.id === id)?.permissions ?? []
		return permissions.includes('change')
	}

	const getDeletePermission = (id: number) => {
		if (!servicePermissionProjects.value.length) return true
		const permissions =
			servicePermissionProjects.value.find((item) => item.id === id)?.permissions ?? []
		return permissions.includes('delete')
	}

	const fieldsFiltered = computed(() => {
		return baseStore.fieldsList.value.filter((field) =>
			filterFields.value.includes(field.parameter)
		)
	})
	return {
		...baseStore,
		servicePermissionProjects,
		filterFields,
		fieldsFiltered,
		drawer,
		calculationDrawer,
		giDrawer,
		resultDrawer,
		userManagementDrawer,
		projectParameterDrawer,
		resetAll,
		resetDrawerWidth,
		toggleDrawer,
		toggleResultDrawer,
		toggleDrawerType,
		servicePermissionItems,
		getChangePermission,
		getDeletePermission
	}
})
